'use client';
import React, { useState, useMemo } from 'react';
import { Shield, DollarSign, Upload, UserCog, Filter, Download, Search, ChevronDown, ChevronUp, Lock, Clock, User, CheckCircle, Eye, Hash, Calendar, X, FileText } from 'lucide-react';
import MetricCard from '@/components/ui/MetricCard';
import StatusBadge from '@/components/ui/StatusBadge';
import { downloadCSV, downloadPDF } from '@/lib/auditExport';


type EventCategory = 'settlement' | 'approval' | 'document_upload' | 'role_action';
type EventOutcome = 'success' | 'rejected' | 'pending' | 'escalated';

interface AuditEntry {
  id: string;
  timestamp: string;
  category: EventCategory;
  action: string;
  actor: string;
  actorRole: string;
  subject: string;
  subjectType: string;
  outcome: EventOutcome;
  reason: string;
  ipAddress: string;
  sessionId: string;
  details: Record<string, string>;
  immutableHash: string;
}

// Immutable audit log data — backend: GET /api/v1/audit/logs?category=&actor=&from=&to=
const auditEntries: AuditEntry[] = [
  {
    id: 'AUD-2026-08201',
    timestamp: '2026-08-20 17:44:12 UTC',
    category: 'settlement',
    action: 'Settlement Batch Executed',
    actor: 'M. Patel',
    actorRole: 'Finance Officer',
    subject: 'BATCH-2026-0820',
    subjectType: 'Settlement Batch',
    outcome: 'success',
    reason: 'All escrow release conditions met; investor confirmations received',
    ipAddress: '10.0.4.22',
    sessionId: 'SES-7F3A9B',
    details: { 'Total Amount': '$4,120,000', 'Investors': '9', 'Campaign': 'Thornfield RE Fund III', 'Escrow Account': 'ESC-2026-0820-T' },
    immutableHash: 'sha256:a3f9c1e2b84d7f0a5c6e3b1d9f2a8c4e7b0d3f6a9c2e5b8d1f4a7c0e3b6d9f2',
  },
  {
    id: 'AUD-2026-08202',
    timestamp: '2026-08-20 16:31:05 UTC',
    category: 'approval',
    action: 'KYC Application Approved',
    actor: 'L. Okafor',
    actorRole: 'Compliance Officer',
    subject: 'KYC-2026-0441 — Priya Narasimhan',
    subjectType: 'KYC Application',
    outcome: 'success',
    reason: 'All identity documents verified; no sanctions matches; risk score within acceptable threshold',
    ipAddress: '10.0.4.11',
    sessionId: 'SES-2C8D4E',
    details: { 'Risk Score': '18 / Low', 'Documents Reviewed': '5 of 5', 'Jurisdiction': 'India', 'Accreditation': 'Verified' },
    immutableHash: 'sha256:b5e8d2f1c4a7e0b3d6f9a2c5e8b1d4f7a0c3e6b9d2f5a8c1e4b7d0f3a6c9e2b5',
  },
  {
    id: 'AUD-2026-08203',
    timestamp: '2026-08-20 15:58:47 UTC',
    category: 'approval',
    action: 'Campaign Approval — Compliance Gate Rejected',
    actor: 'L. Okafor',
    actorRole: 'Compliance Officer',
    subject: 'CAMP-2026-0093 — Meridian Growth REIT',
    subjectType: 'Campaign',
    outcome: 'rejected',
    reason: 'Issuer UBO documentation incomplete; FATF high-risk jurisdiction flag unresolved; EDD required before re-submission',
    ipAddress: '10.0.4.11',
    sessionId: 'SES-2C8D4E',
    details: { 'Gate': 'Compliance', 'Missing Docs': '3', 'Flag': 'FATF High-Risk Jurisdiction', 'Re-submission Allowed': 'Yes' },
    immutableHash: 'sha256:c7a0e3b6d9f2a5c8e1b4d7f0a3c6e9b2d5f8a1c4e7b0d3f6a9c2e5b8d1f4a7c0',
  },
  {
    id: 'AUD-2026-08204',
    timestamp: '2026-08-20 14:22:33 UTC',
    category: 'document_upload',
    action: 'Investor Document Uploaded',
    actor: 'Thornfield Capital Holdings Ltd',
    actorRole: 'Investor (Corporate)',
    subject: 'DOC-2026-TH-0814 — Audited Financials 2025',
    subjectType: 'KYB Document',
    outcome: 'pending',
    reason: 'Uploaded in response to EDD request; pending compliance officer review',
    ipAddress: '185.22.47.103',
    sessionId: 'SES-9A1F2C',
    details: { 'Document Type': 'Audited Financial Statements', 'File Size': '4.2 MB', 'Format': 'PDF', 'Version': 'v1' },
    immutableHash: 'sha256:d9c2e5b8f1a4d7e0b3c6f9a2d5e8b1c4f7a0d3e6b9c2f5a8d1e4b7c0f3a6d9e2',
  },
  {
    id: 'AUD-2026-08205',
    timestamp: '2026-08-20 13:10:19 UTC',
    category: 'role_action',
    action: 'Role Permission Modified',
    actor: 'Admin — R. Blackwood',
    actorRole: 'Platform Administrator',
    subject: 'User: M. Patel — Finance Officer',
    subjectType: 'User Role',
    outcome: 'success',
    reason: 'Expanded settlement execution permissions per CFO authorization REF-2026-0820-CFO',
    ipAddress: '10.0.1.5',
    sessionId: 'SES-4B7E1D',
    details: { 'Permission Added': 'settlement.execute.batch', 'Previous Role': 'Finance Analyst', 'New Role': 'Finance Officer', 'Authorization Ref': 'REF-2026-0820-CFO' },
    immutableHash: 'sha256:e1d4f7a0c3e6b9d2f5a8e1d4f7a0c3e6b9d2f5a8c1e4b7d0f3a6c9e2b5d8f1a4',
  },
  {
    id: 'AUD-2026-08206',
    timestamp: '2026-08-20 11:07:44 UTC',
    category: 'approval',
    action: 'AML Alert Escalated to STR',
    actor: 'M. Patel',
    actorRole: 'Compliance Officer',
    subject: 'AML-2026-0441 — Unknown Beneficial Owner Entity',
    subjectType: 'AML Alert',
    outcome: 'escalated',
    reason: 'UBO not declared; investment blocked; structuring pattern confirmed; STR filing initiated per FinCEN requirements',
    ipAddress: '10.0.4.22',
    sessionId: 'SES-7F3A9B',
    details: { 'Transaction': 'TXN-883104', 'Amount': '$480,000', 'Rule Triggered': 'UBO Not Declared', 'STR Case': 'AML-2026-0441' },
    immutableHash: 'sha256:f3a6c9e2b5d8f1a4c7e0b3d6f9a2c5e8b1d4f7a0c3e6b9d2f5a8c1e4b7d0f3a6',
  },
  {
    id: 'AUD-2026-08207',
    timestamp: '2026-08-20 09:45:02 UTC',
    category: 'settlement',
    action: 'Escrow Release Authorized',
    actor: 'L. Okafor',
    actorRole: 'Compliance Officer',
    subject: 'ESC-2026-0814-T — Thornfield RE Fund II',
    subjectType: 'Escrow Account',
    outcome: 'success',
    reason: 'Post-settlement compliance checks passed; AML screening clear; regulatory reporting filed',
    ipAddress: '10.0.4.11',
    sessionId: 'SES-2C8D4E',
    details: { 'Escrow Balance': '$3,250,000', 'Release Amount': '$3,250,000', 'Beneficiary': 'Thornfield Capital Holdings Ltd', 'SWIFT Ref': 'MT103-884921' },
    immutableHash: 'sha256:a7c0e3b6d9f2a5c8e1b4d7f0a3c6e9b2d5f8a1c4e7b0d3f6a9c2e5b8d1f4a7c0',
  },
  {
    id: 'AUD-2026-08208',
    timestamp: '2026-08-19 18:30:11 UTC',
    category: 'document_upload',
    action: 'NDA Executed and Uploaded',
    actor: 'Hargreaves Investment Co.',
    actorRole: 'Investor (Corporate)',
    subject: 'DOC-2026-HI-NDA-0819 — Campaign NDA',
    subjectType: 'NDA Document',
    outcome: 'success',
    reason: 'Investor executed NDA to access restricted campaign materials for Meridian Growth REIT',
    ipAddress: '82.44.193.21',
    sessionId: 'SES-6D3F8A',
    details: { 'Document Type': 'Non-Disclosure Agreement', 'Campaign': 'Meridian Growth REIT', 'Signed By': 'James Hargreaves (Director)', 'Expiry': '2027-08-19' },
    immutableHash: 'sha256:b9d2f5a8c1e4b7d0f3a6c9e2b5d8f1a4c7e0b3d6f9a2c5e8b1d4f7a0c3e6b9d2',
  },
  {
    id: 'AUD-2026-08209',
    timestamp: '2026-08-19 16:44:58 UTC',
    category: 'approval',
    action: 'Compliance Hold Placed',
    actor: 'L. Okafor',
    actorRole: 'Compliance Officer',
    subject: 'INV-2026-CW-0441 — Crestwood Family Office LLC',
    subjectType: 'Investment',
    outcome: 'pending',
    reason: 'Structuring pattern detected across 3 transactions in 48h; investment frozen pending AML investigation',
    ipAddress: '10.0.4.11',
    sessionId: 'SES-2C8D4E',
    details: { 'Hold Type': 'AML Structuring', 'Amount Frozen': '$1,200,000', 'AML Alert': 'AML-2026-0388', 'Review Deadline': '2026-08-26' },
    immutableHash: 'sha256:c1e4b7d0f3a6c9e2b5d8f1a4c7e0b3d6f9a2c5e8b1d4f7a0c3e6b9d2f5a8c1e4',
  },
  {
    id: 'AUD-2026-08210',
    timestamp: '2026-08-19 14:15:33 UTC',
    category: 'role_action',
    action: 'User Account Suspended',
    actor: 'Admin — R. Blackwood',
    actorRole: 'Platform Administrator',
    subject: 'User: D. Volkov — Investor',
    subjectType: 'User Account',
    outcome: 'success',
    reason: 'Confirmed OFAC SDN list match; account suspended per sanctions compliance protocol; legal notified',
    ipAddress: '10.0.1.5',
    sessionId: 'SES-4B7E1D',
    details: { 'Sanctions List': 'OFAC SDN', 'Match Score': '100%', 'Match Type': 'Exact', 'Legal Ref': 'LEGAL-2026-0819-001' },
    immutableHash: 'sha256:d3f6a9c2e5b8d1f4a7c0e3b6d9f2a5c8e1b4d7f0a3c6e9b2d5f8a1c4e7b0d3f6',
  },
  {
    id: 'AUD-2026-08211',
    timestamp: '2026-08-18 11:20:44 UTC',
    category: 'settlement',
    action: 'Settlement Batch Initiated',
    actor: 'M. Patel',
    actorRole: 'Finance Officer',
    subject: 'BATCH-2026-0818',
    subjectType: 'Settlement Batch',
    outcome: 'pending',
    reason: 'Batch created for Thornfield RE Fund II; awaiting dual-control approval from compliance',
    ipAddress: '10.0.4.22',
    sessionId: 'SES-7F3A9B',
    details: { 'Total Amount': '$3,250,000', 'Investors': '6', 'Campaign': 'Thornfield RE Fund II', 'Requires Approval': 'Yes — Dual Control' },
    immutableHash: 'sha256:e5b8d1f4a7c0e3b6d9f2a5c8e1b4d7f0a3c6e9b2d5f8a1c4e7b0d3f6a9c2e5b8',
  },
  {
    id: 'AUD-2026-08212',
    timestamp: '2026-08-17 09:05:17 UTC',
    category: 'document_upload',
    action: 'Settlement Confirmation Uploaded',
    actor: 'System — Settlement Processor',
    actorRole: 'Automated Process',
    subject: 'DOC-2026-SC-0817 — BATCH-2026-0814 Confirmation',
    subjectType: 'Settlement Document',
    outcome: 'success',
    reason: 'Auto-generated settlement confirmation after successful fund transfer; stored in investor document vaults',
    ipAddress: '10.0.0.1',
    sessionId: 'SES-SYS-AUTO',
    details: { 'Document Type': 'Settlement Confirmation', 'Batch Ref': 'BATCH-2026-0814', 'Investors Notified': '6', 'Storage': 'Document Vault — Settlement' },
    immutableHash: 'sha256:f7a0c3e6b9d2f5a8c1e4b7d0f3a6c9e2b5d8f1a4c7e0b3d6f9a2c5e8b1d4f7a0',
  },
];

const categoryConfig: Record<EventCategory, { label: string; icon: React.ElementType; color: string; bg: string }> = {
  settlement: { label: 'Settlement', icon: DollarSign, color: 'text-positive', bg: 'bg-positive/10' },
  approval: { label: 'Approval', icon: CheckCircle, color: 'text-info', bg: 'bg-info/10' },
  document_upload: { label: 'Document Upload', icon: Upload, color: 'text-warning', bg: 'bg-warning/10' },
  role_action: { label: 'Role Action', icon: UserCog, color: 'text-critical', bg: 'bg-critical/10' },
};

const outcomeConfig: Record<EventOutcome, { label: string; variant: 'active' | 'rejected' | 'pending' | 'warning' }> = {
  success: { label: 'Success', variant: 'active' },
  rejected: { label: 'Rejected', variant: 'rejected' },
  pending: { label: 'Pending', variant: 'pending' },
  escalated: { label: 'Escalated', variant: 'warning' },
};

export default function AuditLogViewer() {
  const [searchActor, setSearchActor] = useState('');
  const [searchReason, setSearchReason] = useState('');
  const [filterCategory, setFilterCategory] = useState<EventCategory | 'all'>('all');
  const [filterOutcome, setFilterOutcome] = useState<EventOutcome | 'all'>('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const filtered = useMemo(() => {
    return auditEntries.filter((e) => {
      if (filterCategory !== 'all' && e.category !== filterCategory) return false;
      if (filterOutcome !== 'all' && e.outcome !== filterOutcome) return false;
      if (searchActor && !e.actor.toLowerCase().includes(searchActor.toLowerCase())) return false;
      if (searchReason && !e.reason.toLowerCase().includes(searchReason.toLowerCase()) && !e.action.toLowerCase().includes(searchReason.toLowerCase())) return false;
      if (filterDateFrom && e.timestamp < filterDateFrom) return false;
      if (filterDateTo && e.timestamp > filterDateTo + ' 23:59:59') return false;
      return true;
    });
  }, [searchActor, searchReason, filterCategory, filterOutcome, filterDateFrom, filterDateTo]);

  const activeFilterCount = [
    filterCategory !== 'all',
    filterOutcome !== 'all',
    searchActor,
    searchReason,
    filterDateFrom,
    filterDateTo,
  ].filter(Boolean).length;

  const clearFilters = () => {
    setSearchActor('');
    setSearchReason('');
    setFilterCategory('all');
    setFilterOutcome('all');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const exportCols = [
    { key: 'id', label: 'Event ID' },
    { key: 'timestamp', label: 'Timestamp' },
    { key: 'category', label: 'Category' },
    { key: 'action', label: 'Action' },
    { key: 'actor', label: 'Actor' },
    { key: 'actorRole', label: 'Actor Role' },
    { key: 'subject', label: 'Subject' },
    { key: 'subjectType', label: 'Subject Type' },
    { key: 'outcome', label: 'Outcome' },
    { key: 'reason', label: 'Reason' },
    { key: 'ipAddress', label: 'IP Address' },
    { key: 'sessionId', label: 'Session ID' },
    { key: 'immutableHash', label: 'Integrity Hash' },
  ];

  const handleExportCSV = () => {
    downloadCSV(filtered, exportCols, `audit-trail-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const handleExportPDF = () => {
    const pdfCols = [
      { key: 'id', label: 'Event ID' },
      { key: 'timestamp', label: 'Timestamp' },
      { key: 'category', label: 'Category' },
      { key: 'action', label: 'Action' },
      { key: 'actor', label: 'Actor' },
      { key: 'actorRole', label: 'Role' },
      { key: 'outcome', label: 'Outcome' },
      { key: 'reason', label: 'Reason' },
      { key: 'ipAddress', label: 'IP' },
    ];
    downloadPDF(filtered, pdfCols, 'Immutable Audit Trail — Regulatory Export', 'CrowdVault™ Platform — Tamper-Evident Record');
  };

  const metrics = useMemo(() => ({
    total: auditEntries.length,
    settlements: auditEntries.filter(e => e.category === 'settlement').length,
    approvals: auditEntries.filter(e => e.category === 'approval').length,
    roleActions: auditEntries.filter(e => e.category === 'role_action').length,
  }), []);

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Lock size={16} className="text-primary" />
            </div>
            <h1 className="text-xl font-bold text-foreground">Immutable Audit Log</h1>
          </div>
          <p className="text-sm text-muted-foreground">Tamper-evident record of all platform events — settlements, approvals, document uploads, and role actions</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-positive/10 border border-positive/20">
            <Shield size={13} className="text-positive" />
            <span className="text-xs font-semibold text-positive">Integrity Verified</span>
          </div>
          <button onClick={handleExportCSV} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-3 border border-border text-xs font-medium text-foreground hover:bg-surface-2 transition-colors">
            <Download size={13} />
            Export CSV
          </button>
          <button onClick={handleExportPDF} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-3 border border-border text-xs font-medium text-foreground hover:bg-surface-2 transition-colors">
            <FileText size={13} />
            Export PDF
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Total Events" value={String(metrics.total)} subValue="All time" icon={Hash} variant="default" mono />
        <MetricCard label="Settlements" value={String(metrics.settlements)} subValue="Fund movements" icon={DollarSign} variant="info" mono />
        <MetricCard label="Approvals / Rejections" value={String(metrics.approvals)} subValue="Decisions logged" icon={CheckCircle} variant="default" mono />
        <MetricCard label="Role Actions" value={String(metrics.roleActions)} subValue="Permission changes" icon={UserCog} variant="warning" mono />
      </div>

      {/* Filter Bar */}
      <div className="glass-card rounded-xl p-4">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Actor search */}
          <div className="relative flex-1 min-w-[180px]">
            <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by actor..."
              value={searchActor}
              onChange={e => setSearchActor(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm bg-surface-3 border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
            />
          </div>

          {/* Reason / action search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search action or reason..."
              value={searchReason}
              onChange={e => setSearchReason(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm bg-surface-3 border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
            />
          </div>

          {/* Category filter */}
          <select
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value as EventCategory | 'all')}
            className="px-3 py-2 text-sm bg-surface-3 border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
          >
            <option value="all">All Categories</option>
            <option value="settlement">Settlement</option>
            <option value="approval">Approval</option>
            <option value="document_upload">Document Upload</option>
            <option value="role_action">Role Action</option>
          </select>

          {/* Outcome filter */}
          <select
            value={filterOutcome}
            onChange={e => setFilterOutcome(e.target.value as EventOutcome | 'all')}
            className="px-3 py-2 text-sm bg-surface-3 border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
          >
            <option value="all">All Outcomes</option>
            <option value="success">Success</option>
            <option value="rejected">Rejected</option>
            <option value="pending">Pending</option>
            <option value="escalated">Escalated</option>
          </select>

          {/* Date range toggle */}
          <button
            onClick={() => setShowFilters(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${showFilters ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-surface-3 border-border text-foreground hover:bg-surface-2'}`}
          >
            <Calendar size={14} />
            Date Range
            {showFilters ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>

          {activeFilterCount > 0 && (
            <button onClick={clearFilters} className="flex items-center gap-1 px-2.5 py-2 rounded-lg bg-critical/10 border border-critical/20 text-critical text-xs font-medium hover:bg-critical/20 transition-colors">
              <X size={12} />
              Clear ({activeFilterCount})
            </button>
          )}
        </div>

        {showFilters && (
          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border flex-wrap">
            <div className="flex items-center gap-2">
              <Clock size={13} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground font-medium">From:</span>
              <input
                type="date"
                value={filterDateFrom}
                onChange={e => setFilterDateFrom(e.target.value)}
                className="px-3 py-1.5 text-sm bg-surface-3 border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground font-medium">To:</span>
              <input
                type="date"
                value={filterDateTo}
                onChange={e => setFilterDateTo(e.target.value)}
                className="px-3 py-1.5 text-sm bg-surface-3 border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
              />
            </div>
          </div>
        )}
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Showing <span className="font-semibold text-foreground">{filtered.length}</span> of <span className="font-semibold text-foreground">{auditEntries.length}</span> events
        </p>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-positive animate-pulse" />
          <span className="text-xs text-muted-foreground">Live — updates in real-time</span>
        </div>
      </div>

      {/* Audit Log Table */}
      <div className="glass-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-3">
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground whitespace-nowrap">Timestamp</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Category</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Action</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Actor</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Subject</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Outcome</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Details</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-muted-foreground text-sm">
                    No audit events match the current filters.
                  </td>
                </tr>
              ) : (
                filtered.map((entry) => {
                  const cat = categoryConfig[entry.category];
                  const CatIcon = cat.icon;
                  const outcome = outcomeConfig[entry.outcome];
                  const isExpanded = expandedId === entry.id;

                  return (
                    <React.Fragment key={entry.id}>
                      <tr
                        className={`border-b border-border/50 hover:bg-surface-3/50 transition-colors cursor-pointer ${isExpanded ? 'bg-surface-3/30' : ''}`}
                        onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                      >
                        {/* Timestamp */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <p className="text-xs font-mono-data text-foreground">{entry.timestamp.split(' ')[0]}</p>
                          <p className="text-xs font-mono-data text-muted-foreground">{entry.timestamp.split(' ')[1]} {entry.timestamp.split(' ')[2]}</p>
                        </td>

                        {/* Category */}
                        <td className="px-4 py-3">
                          <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md ${cat.bg}`}>
                            <CatIcon size={12} className={cat.color} />
                            <span className={`text-xs font-medium ${cat.color}`}>{cat.label}</span>
                          </div>
                        </td>

                        {/* Action */}
                        <td className="px-4 py-3 max-w-[220px]">
                          <p className="text-sm font-medium text-foreground leading-snug">{entry.action}</p>
                          <p className="text-xs text-muted-foreground font-mono-data mt-0.5">{entry.id}</p>
                        </td>

                        {/* Actor */}
                        <td className="px-4 py-3">
                          <p className="text-sm text-foreground font-medium">{entry.actor}</p>
                          <p className="text-xs text-muted-foreground">{entry.actorRole}</p>
                        </td>

                        {/* Subject */}
                        <td className="px-4 py-3 max-w-[200px]">
                          <p className="text-sm text-foreground truncate">{entry.subject}</p>
                          <p className="text-xs text-muted-foreground">{entry.subjectType}</p>
                        </td>

                        {/* Outcome */}
                        <td className="px-4 py-3">
                          <StatusBadge variant={outcome.variant} label={outcome.label} dot />
                        </td>

                        {/* Expand */}
                        <td className="px-4 py-3">
                          <button className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium transition-colors">
                            <Eye size={13} />
                            {isExpanded ? 'Hide' : 'View'}
                          </button>
                        </td>
                      </tr>

                      {/* Expanded Detail Row */}
                      {isExpanded && (
                        <tr className="border-b border-border bg-surface-3/20">
                          <td colSpan={7} className="px-4 py-4">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                              {/* Reason */}
                              <div className="lg:col-span-2">
                                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Reason / Justification</p>
                                <p className="text-sm text-foreground leading-relaxed bg-surface-3 rounded-lg px-3 py-2.5 border border-border">{entry.reason}</p>

                                {/* Event Details */}
                                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-3 mb-2">Event Details</p>
                                <div className="grid grid-cols-2 gap-2">
                                  {Object.entries(entry.details).map(([k, v]) => (
                                    <div key={k} className="flex items-start gap-2 bg-surface-3 rounded-lg px-3 py-2 border border-border">
                                      <span className="text-xs text-muted-foreground font-medium min-w-0 flex-shrink-0">{k}:</span>
                                      <span className="text-xs text-foreground font-mono-data truncate">{v}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Integrity & Session */}
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Integrity Proof</p>
                                <div className="bg-surface-3 rounded-lg px-3 py-2.5 border border-border mb-3">
                                  <div className="flex items-center gap-1.5 mb-1.5">
                                    <Shield size={12} className="text-positive" />
                                    <span className="text-xs font-semibold text-positive">Hash Verified</span>
                                  </div>
                                  <p className="text-xs font-mono-data text-muted-foreground break-all leading-relaxed">{entry.immutableHash}</p>
                                </div>

                                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Session Info</p>
                                <div className="bg-surface-3 rounded-lg px-3 py-2.5 border border-border space-y-1.5">
                                  <div className="flex justify-between">
                                    <span className="text-xs text-muted-foreground">Session ID</span>
                                    <span className="text-xs font-mono-data text-foreground">{entry.sessionId}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-xs text-muted-foreground">IP Address</span>
                                    <span className="text-xs font-mono-data text-foreground">{entry.ipAddress}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-xs text-muted-foreground">Event ID</span>
                                    <span className="text-xs font-mono-data text-foreground">{entry.id}</span>
                                  </div>
                                </div>

                                <div className="mt-3 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-surface-3 border border-border">
                                  <Lock size={11} className="text-muted-foreground" />
                                  <span className="text-xs text-muted-foreground">Record is immutable — cannot be modified or deleted</span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
